import React, { useState, useEffect, useRef } from "react";
import ReactPlayer from "react-player";
import styles from "./LivePage.module.css";

function LiveTvPage() {
  const [channelsData, setChannelsData] = useState([]);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [showMenu, setShowMenu] = useState(false);
  const [search, setSearch] = useState("");
  const [loadingPlayer, setLoadingPlayer] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [error, setError] = useState(null);
  const [playerError, setPlayerError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("All channels");
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(true);
  const playerRef = useRef();

  const isIOS = () => {
    if (typeof navigator === "undefined") return false;
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  };

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoadingData(true);
        const response = await fetch("/data/channelsData.json");
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        setChannelsData(data);
        setError(null);
      } catch (e) {
        setError("Failed to fetch channel data. Please try again later.");
        console.error("Fetching channels failed:", e);
      } finally {
        setLoadingData(false);
      }
    };

    fetchChannels();
  }, []);

  useEffect(() => {
    if (selectedChannel) {
      setLoadingPlayer(true);
      setPlayerError(null);
      setIsPlaying(true);
      setIsMuted(true);
    } else {
      setIsPlaying(false);
    }
  }, [selectedChannel]);

  const handleReady = () => {
    setLoadingPlayer(false);
    setPlayerError(null);
  };

  const handlePlayerError = (e) => {
    console.error("ReactPlayer error:", e);
    setLoadingPlayer(false);
    setPlayerError(
      "An error occurred while launching the channel. It may be currently unavailable or the link may be invalid."
    );
  };

  const categories = React.useMemo(() => {
    if (loadingData) return ["All channels"];
    const allCategories = new Set(["All channels"]);
    channelsData.forEach((channel) => {
      if (Array.isArray(channel.tags)) {
        channel.tags.forEach((tag) =>
          allCategories.add(tag.charAt(0).toUpperCase() + tag.slice(1).toLowerCase())
        );
      }
    });
    return Array.from(allCategories).sort((a, b) => {
      if (a === "All channels") return -1;
      if (b === "All channels") return 1;
      return a.localeCompare(b);
    });
  }, [channelsData, loadingData]);

  const filteredChannels = React.useMemo(() => {
    if (loadingData) return [];
    const lowercasedSearchTerm = search.toLowerCase();
    return channelsData.filter((ch) => {
      const matchesSearch = ch.name.toLowerCase().includes(lowercasedSearchTerm);
      const matchesCategory =
        selectedCategory === "All channels" ||
        (Array.isArray(ch.tags) &&
          ch.tags.some((tag) => tag.toLowerCase() === selectedCategory.toLowerCase()));
      return matchesSearch && matchesCategory;
    });
  }, [search, selectedCategory, channelsData, loadingData]);

  return (
    <div className={styles.liveTvContainer}>
      <div className={styles.playerWrapper}>
        {selectedChannel ? (
          <>
            <ReactPlayer
              ref={playerRef}
              url={selectedChannel.url}
              playing={isPlaying}
              muted={isMuted}
              controls={true}
              width="100%"
              height="100%"
              playsinline={true}
              config={{
                file: {
                  forceHLS: !isIOS(),
                  forceVideo: true,
                  attributes: {
                    playsInline: true,
                    controls: true,
                    muted: isMuted,
                    autoPlay: true,
                    xWebkitAirplay: "allow",
                    webkitAirplay: "allow",
                  },
                },
              }}
              onReady={handleReady}
              onError={handlePlayerError}
              className={styles.reactPlayer}
            />

            {loadingPlayer && (
              <div className={styles.playerLoader}>
                <div className={styles.spinner}></div>
                <p>Loading...</p>
              </div>
            )}

            {playerError && !loadingPlayer && (
              <p className={styles.playerErrorMessage}>{playerError}</p>
            )}
          </>
        ) : (
          <div className={styles.noChannelSelected}>
            <i className={`fas fa-tv ${styles.noChannelIcon}`}></i>
            <p>Choose a channel to start broadcasting by clicking on the three lines at the bottom</p>
          </div>
        )}
      </div>

      <button
        className={`${styles.fabMenuButton} ${showMenu ? styles.fabOpen : ""}`}
        onClick={() => setShowMenu(!showMenu)}
        aria-label={showMenu ? "Close Channel List" : "Open Channel List"}
      >
        <i className="fas fa-bars"></i>
      </button>

      {showMenu && (
        <div className={styles.menuOverlay} onClick={() => setShowMenu(false)}>
          <div
            className={styles.menuContent}
            onClick={(e) => e.stopPropagation()}
          >
            <h2>Channels</h2>

            <div className={styles.menuCategories}>
              {loadingData ? (
                <p>Loading categories...</p>
              ) : error ? (
                <p className={styles.errorMessage}>{error}</p>
              ) : (
                categories.map((cat) => (
                  <button
                    key={cat}
                    className={`${styles.menuCategoryButton} ${
                      selectedCategory === cat ? styles.selectedCategory : ""
                    }`}
                    onClick={() => setSelectedCategory(cat)}
                    aria-pressed={selectedCategory === cat}
                  >
                    {cat}
                  </button>
                ))
              )}
            </div>

            <div className={styles.menuSearchBar}>
              <i className={`fas fa-search ${styles.menuSearchIcon}`}></i>
              <input
                type="text"
                placeholder="Search for channel..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className={styles.menuSearchInput}
                aria-label="Channel Search"
              />
            </div>

            <div className={styles.menuChannelsScrollArea}>
              <div className={styles.menuChannelsGrid}>
                {loadingData ? (
                  <p className={styles.loadingMessage}>Loading channels...</p>
                ) : error ? (
                  <p className={styles.errorMessage}>{error}</p>
                ) : filteredChannels.length > 0 ? (
                  filteredChannels.map((ch) => (
                    <div
                      key={ch.id}
                      className={`${styles.menuChannelCard} ${
                        selectedChannel?.id === ch.id ? styles.selected : ""
                      }`}
                      onClick={() => {
                        setSelectedChannel(ch);
                        setShowMenu(false);
                      }}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          setSelectedChannel(ch);
                          setShowMenu(false);
                        }
                      }}
                      aria-current={selectedChannel?.id === ch.id}
                    >
                      <img
                        src={ch.logo}
                        alt={ch.name}
                        className={styles.menuChannelLogo}
                      />
                      <p className={styles.menuChannelName}>{ch.name}</p>
                    </div>
                  ))
                ) : (
                  <p className={styles.noMenuResults}>No results found</p>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LiveTvPage;
